import type { AccountView } from "@kwp/contracts";
import { useId } from "react";
import type { MyWorkspace } from "../../../lib/me";
import { Button } from "../../../ui";
import { ACTION_CHOICES, needsValue, type ActionDraft, type ActionKind, type ScopeChoice } from "./model";

const field = "min-h-9 rounded-md border border-border bg-surface px-3 text-sm text-text";

const PLACEHOLDER = { tag: "Receipts", folder: "Old newsletters", address: "someone@example.com" } as const;

/** The actions builder: one row per action, each a kind and (for tag / move / forward) a value. */
export function ActionsBuilder({ value, onChange }: { value: readonly ActionDraft[]; onChange: (next: ActionDraft[]) => void }) {
  const id = useId();
  const set = (i: number, patch: Partial<ActionDraft>) => onChange(value.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-text">Then</legend>
      {value.map((d, i) => {
        const needs = needsValue(d.kind);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor={`${id}-kind-${i}`}>
              Action {i + 1}
            </label>
            <select id={`${id}-kind-${i}`} className={field} value={d.kind} onChange={(e) => set(i, { kind: e.target.value as ActionKind, value: "" })}>
              {ACTION_CHOICES.map((c) => (
                <option key={c.kind} value={c.kind}>
                  {c.label}
                </option>
              ))}
            </select>
            {needs === null ? null : (
              <>
                <label className="sr-only" htmlFor={`${id}-value-${i}`}>
                  {needs === "address" ? `Forward address for action ${i + 1}` : `${needs === "tag" ? "Tag" : "Folder"} for action ${i + 1}`}
                </label>
                <input
                  id={`${id}-value-${i}`}
                  className={`${field} min-w-0 flex-1`}
                  type={needs === "address" ? "email" : "text"}
                  placeholder={PLACEHOLDER[needs]}
                  maxLength={100}
                  value={d.value}
                  onChange={(e) => set(i, { value: e.target.value })}
                />
              </>
            )}
            {value.length > 1 ? (
              <Button variant="ghost" aria-label={`Remove action ${i + 1}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
                Remove
              </Button>
            ) : null}
          </div>
        );
      })}
      {value.length < 10 ? (
        <div>
          <Button variant="ghost" onClick={() => onChange([...value, { kind: "markRead", value: "" }])}>
            Add an action
          </Button>
        </div>
      ) : null}
    </fieldset>
  );
}

/** Which accounts a rule covers: all, one workspace's, or picked accounts. */
export function ScopePicker({
  value,
  onChange,
  accounts,
  workspaces,
}: {
  value: ScopeChoice;
  onChange: (next: ScopeChoice) => void;
  accounts: readonly AccountView[];
  workspaces: readonly MyWorkspace[];
}) {
  const name = useId();
  const firstWorkspace = workspaces[0];
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-text">On</legend>
      <label className="flex items-center gap-2 text-sm text-text">
        <input type="radio" name={name} checked={value.kind === "all"} onChange={() => onChange({ kind: "all" })} />
        All my accounts
      </label>
      {firstWorkspace === undefined ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-text">
            <input
              type="radio"
              name={name}
              checked={value.kind === "workspace"}
              onChange={() => onChange({ kind: "workspace", workspaceId: firstWorkspace.id })}
            />
            The accounts of workspace
          </label>
          {value.kind === "workspace" ? (
            <select aria-label="Workspace" className={field} value={value.workspaceId} onChange={(e) => onChange({ kind: "workspace", workspaceId: e.target.value })}>
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-text">
        <input
          type="radio"
          name={name}
          checked={value.kind === "accounts"}
          onChange={() => onChange({ kind: "accounts", accountIds: accounts.slice(0, 1).map((a) => a.id) })}
        />
        These accounts
      </label>
      {value.kind === "accounts" ? (
        <div className="ml-6 flex flex-col gap-1">
          {accounts.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={value.accountIds.includes(a.id)}
                onChange={(e) =>
                  onChange({ kind: "accounts", accountIds: e.target.checked ? [...value.accountIds, a.id] : value.accountIds.filter((x) => x !== a.id) })
                }
              />
              {a.address}
            </label>
          ))}
        </div>
      ) : null}
    </fieldset>
  );
}
